// SearchDetailPage — fiche détaillée UNIVERSELLE AYA Search (v5.14.0).
// /fiche/:docId → PHOTO · CATÉGORIE · NOM · STATUT · INFOS · LOCALISATION · CARTE ·
// HORAIRES · CONTACT · SERVICES · ACTIONS. Adaptée par catégorie, aucune donnée inventée.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Mail, MapPin, Navigation, Phone, Share2 } from "lucide-react";
import { toast } from "sonner";
import { LocalPackMap } from "@/components/LocalPackMap";
import FicheAppointment from "@/components/FicheAppointment";
import FicheReviews from "@/components/FicheReviews";

const API = process.env.REACT_APP_BACKEND_URL;

const EXTRA_LABELS = {
  statut: "Statut", toge: "Toge", specialite: "Spécialité", etablissement: "Établissement",
  annees_experience: "Expérience", honoraires: "Honoraires estimés", langues: "Langues",
  organism: "Organisme", cost_display: "Coût", duration: "Délai", type: "Type",
  rate_display: "Taux", availability: "Disponibilité", stars: "Étoiles",
  telephone_fixe: "Téléphone fixe", source: "Source", company: "Entreprise",
  contract_type: "Contrat", experience_level: "Niveau", salary_display: "Salaire",
};

export default function SearchDetailPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/v3/search/doc/${encodeURIComponent(docId)}`)
      .then((res) => setR(res.data.result))
      .catch(() => setErr(true));
  }, [docId]);

  const onShare = async () => {
    const text = `${r.name}${r.commune_name ? " — " + r.commune_name : ""} (via AYA)`;
    if (navigator.share) { try { await navigator.share({ text }); } catch { /* annulé */ } }
    else { await navigator.clipboard?.writeText(text); toast.success("Copié ✓"); }
  };

  if (err) return (
    <div style={{ ...P.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }} data-testid="fiche-not-found">
      <div style={{ color: "#9DB4A6", fontSize: 14 }}>Fiche introuvable.</div>
      <button onClick={() => navigate("/search")} style={P.backPill}>← Retour à la recherche</button>
    </div>
  );
  if (!r) return <div style={{ ...P.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#9DB4A6" }}>Chargement…</div>;

  const x = r.extra || {};
  const caps = r.capabilities || {};
  const extraRows = Object.entries(x).filter(([, v]) => v != null && (typeof v === "string" || typeof v === "number"));

  return (
    <div style={P.page} data-testid={`fiche-page-${r.id}`}>
      {/* PHOTO hero */}
      <div style={{ position: "relative" }}>
        {r.image ? (
          <img src={r.image} alt={r.image_alt || r.name} style={P.hero} onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="fiche-photo" />
        ) : (
          <div style={{ ...P.hero, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 44 }}>{r.category_emoji || "📍"}</div>
        )}
        <button onClick={() => navigate(-1)} style={P.backBtn} data-testid="fiche-back-btn" aria-label="Retour"><ArrowLeft size={18} /></button>
        {r.is_mock && <span style={{ ...P.testBadge }} data-testid="fiche-test-badge">TEST</span>}
      </div>

      <div style={P.body}>
        {/* Badges + nom + statut */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {r.category_label && <span style={{ ...P.badge, background: "rgba(16,185,129,0.15)", color: "#6EE7B7" }}>{r.category_emoji} {String(r.category_label).toUpperCase()}</span>}
          {r.provenance === "partner" && <span style={{ ...P.badge, background: "linear-gradient(135deg, #D4AF37, #B8860B)", color: "#1A1206" }} data-testid="fiche-partner-badge">★ PARTENAIRE AYA</span>}
          {r.verified && <span style={{ ...P.badge, background: "rgba(16,185,129,0.2)", color: "#34D399" }}>VÉRIFIÉ</span>}
          {x.garde && <span style={{ ...P.badge, background: "rgba(239,68,68,0.2)", color: "#FCA5A5" }}>🚨 DE GARDE</span>}
        </div>
        <h1 style={P.h1} data-testid="fiche-name">{r.name}</h1>
        <div style={P.meta}>
          {r.rating != null && <span style={{ color: "#EFC75E", fontWeight: 800 }}>⭐ {r.rating}{r.reviews_count ? ` (${r.reviews_count})` : ""}</span>}
          {r.commune_name && <span><MapPin size={12} style={{ verticalAlign: -1 }} /> {r.commune_name}</span>}
          {r.price_display || (r.price_from != null && <span style={{ color: "#EFC75E", fontWeight: 800 }}>dès {Number(r.price_from).toLocaleString("fr-FR")} F</span>)}
          {r.price_display && <span style={{ color: "#EFC75E", fontWeight: 800 }}>{r.price_display}</span>}
        </div>
        {r.description && <p style={P.desc}>{r.description}</p>}

        {/* ACTIONS */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0 4px" }}>
          {r.cabinet_slug && (
            <button onClick={() => navigate(`/cabinet/${r.cabinet_slug}?src=aya_search`)} data-testid="fiche-enter-cabinet"
              style={{ ...P.action, background: "#10B981", color: "#04120B", border: "none", fontWeight: 800 }}>🏛 Entrer dans le cabinet</button>
          )}
          {caps.call && r.tel && (
            <a href={`tel:${String(r.tel).replace(/\s/g, "")}`} style={{ ...P.action, background: "#F97316", color: "#1A1206", border: "none", fontWeight: 800 }} data-testid="fiche-call"><Phone size={14} /> Appeler</a>
          )}
          {caps.directions && r.lat != null && r.lng != null && (
            <button onClick={() => navigate(`/map-explorer?lat=${r.lat}&lng=${r.lng}&label=${encodeURIComponent(r.name)}`)} style={P.action} data-testid="fiche-route"><Navigation size={14} /> Itinéraire</button>
          )}
          {r.email && <a href={`mailto:${r.email}`} style={P.action} data-testid="fiche-email"><Mail size={14} /> Écrire</a>}
          <button onClick={onShare} style={P.action} data-testid="fiche-share"><Share2 size={14} /> Partager</button>
        </div>

        {/* INFORMATIONS */}
        <section style={P.section} data-testid="fiche-infos">
          <h2 style={P.h2}>Informations</h2>
          {r.address && <div style={P.row}><MapPin size={13} color="#34D399" /><span>{r.address}</span></div>}
          {r.hours && <div style={P.row}><Clock size={13} color="#34D399" /><span>{r.hours}</span></div>}
          {r.tel && <div style={P.row}><Phone size={13} color="#34D399" /><span>{r.tel}</span></div>}
          {r.email && <div style={P.row}><Mail size={13} color="#34D399" /><span>{r.email}</span></div>}
          {extraRows.map(([k, v]) => (
            <div key={k} style={P.row}><span style={{ color: "#6B7F72", minWidth: 110 }}>{EXTRA_LABELS[k] || k}</span><span>{k === "stars" ? "★".repeat(Number(v) || 0) : String(v)}</span></div>
          ))}
          {Array.isArray(r.specialties) && r.specialties.length > 0 && (
            <div style={P.row}><span style={{ color: "#6B7F72", minWidth: 110 }}>Services</span><span>{r.specialties.join(" · ")}</span></div>
          )}
          {Array.isArray(x.services) && x.services.length > 0 && (
            <div style={P.row}><span style={{ color: "#6B7F72", minWidth: 110 }}>Services</span><span>{x.services.join(" · ")}</span></div>
          )}
        </section>

        {/* RENDEZ-VOUS — générique (capabilities.appointment, ex. Cabinet Allegra) */}
        {caps.appointment && <FicheAppointment result={r} />}

        {/* LOCALISATION */}
        {r.lat != null && r.lng != null && (
          <section style={P.section} data-testid="fiche-map">
            <h2 style={P.h2}>Localisation</h2>
            <LocalPackMap results={[r]} selectedId={r.id} onSelect={() => {}} />
          </section>
        )}

        {/* AVIS — uniquement sur les établissements réels */}
        {!r.is_mock && <FicheReviews result={r} />}

        {r.is_mock && (
          <div style={{ fontSize: 11, color: "#F59E0B", margin: "10px 0 20px" }}>
            ⚠️ Donnée de démonstration AYA (TEST) — ne correspond pas à un établissement réel.
          </div>
        )}
      </div>
    </div>
  );
}

const P = {
  page: { minHeight: "100vh", background: "radial-gradient(ellipse at top, #072115 0%, #04120B 60%, #030B07 100%)", color: "#ECF5EE", paddingBottom: 30 },
  hero: { width: "100%", height: 210, objectFit: "cover", display: "block", background: "radial-gradient(120% 140% at 20% 0%, rgba(16,185,129,0.22), rgba(6,40,26,0.92) 60%, #04120B 100%)" },
  backBtn: { position: "absolute", top: 12, left: 12, width: 36, height: 36, borderRadius: "50%", border: "1px solid rgba(16,185,129,0.4)", background: "rgba(4,18,11,0.8)", color: "#ECF5EE", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  backPill: { padding: "10px 18px", borderRadius: 999, border: "1px solid rgba(16,185,129,0.4)", background: "rgba(8,38,24,0.8)", color: "#34D399", fontWeight: 700, cursor: "pointer" },
  testBadge: { position: "absolute", top: 12, right: 12, padding: "4px 11px", borderRadius: 999, background: "#F59E0B", color: "#1A1206", fontSize: 10, fontWeight: 900, letterSpacing: 1 },
  body: { maxWidth: 680, margin: "0 auto", padding: "14px 16px 0" },
  badge: { display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.7 },
  h1: { margin: "9px 0 4px", fontSize: 21, fontWeight: 900, lineHeight: 1.25 },
  meta: { display: "flex", gap: 10, flexWrap: "wrap", color: "#9DB4A6", fontSize: 12.5, alignItems: "center" },
  desc: { color: "#AFC4B6", fontSize: 13, lineHeight: 1.5, margin: "8px 0 0" },
  action: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 999, border: "1px solid rgba(16,185,129,0.4)", background: "rgba(8,38,24,0.7)", color: "#CFE5D6", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textDecoration: "none" },
  section: { marginTop: 16, padding: 13, borderRadius: 14, background: "rgba(8,38,24,0.6)", border: "1px solid rgba(16,185,129,0.18)" },
  h2: { margin: "0 0 9px", fontSize: 13.5, fontWeight: 800, color: "#ECF5EE" },
  row: { display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: "#CFE5D6", padding: "4px 0", lineHeight: 1.45 },
};
